import { startTransition, useDeferredValue, useState, ViewTransition } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { Message, PublicUser } from '@avslack/shared';
import { getPinned } from '../lib/api';
import { formatDay } from '../lib/format';
import { Icon } from './Icon';
import { useUsers } from '../data/users';

/**
 * Kituzott uzenetek savja a csatorna tetejen.
 *
 * Alapbol osszecsukva egyetlen sor - a kituzes emlekezteto, nem allando
 * tartalom. Kinyitva viszont egy kattintassal a helyere lehet ugrani.
 */
export function PinnedBar({
  channelId,
  meId,
  usersById,
}: {
  channelId: string;
  meId: string;
  usersById: Map<string, PublicUser>;
}) {
  const { nameOf } = useUsers();
  const [open, setOpen] = useState(false);

  const { data: loaded } = useQuery({
    queryKey: ['pins', channelId],
    queryFn: () => getPinned(channelId),
    staleTime: 60_000,
  });

  /*
    A sav MEGJELENESE (elso kituzes) es ELTUNESE (utolso levetel) atmenet:
    becsuszik, kicsuszik - latszik, hogy most tortent valami.

    A halasztott ertek a hordozo: a halasztott render atmenetkent fut. Csatorna-
    valtaskor es az elso betolteskor viszont az ELO erteket mutatjuk - ott nem
    "most lett" kituzes, csak megnyitottuk a csatornat.
  */
  const deferredChannelId = useDeferredValue(channelId);
  const deferredPinned = useDeferredValue(loaded);
  const pinned =
    deferredChannelId === channelId && deferredPinned !== undefined ? deferredPinned : loaded;

  if (!pinned || pinned.length === 0) return null;

  return (
    <ViewTransition enter="slide-down-in" exit="slide-up-out" default="none">
      <div className="border-b border-border-soft bg-surface-sunken">
        <button
          type="button"
          // A lista lecsuszik / felhuzodik - atmenetkent (lent).
          onClick={() => startTransition(() => setOpen((value) => !value))}
          aria-expanded={open}
          className="flex w-full items-center gap-2 px-4 py-1.5 text-left text-xs text-ink-muted transition-colors duration-(--duration-quick) hover:text-ink sm:px-6"
        >
          <span aria-hidden="true" className="text-alert">
            <Icon name="kituzes" />
          </span>
          <span className="font-semibold">{pinned.length} kitűzött üzenet</span>
          <span
            aria-hidden="true"
            className="ml-auto inline-block transition-transform duration-(--duration-base) ease-(--ease-soft)"
            style={{ transform: open ? 'none' : 'rotate(-90deg)' }}
          >
            <Icon name="le" />
          </span>
        </button>

        {open && (
          <ViewTransition enter="slide-down-in" exit="slide-up-out" default="none">
            <ul className="max-h-56 space-y-1 overflow-y-auto px-4 pb-2 sm:px-6">
              {pinned.map((message: Message) => {
                const author = message.userId ? usersById.get(message.userId) : undefined;
                const preview =
                  message.text.length > 140 ? `${message.text.slice(0, 140)}…` : message.text;

                return (
                  <li key={message.id}>
                    <Link
                      to="/c/$channelId"
                      params={{ channelId }}
                      search={{ m: message.id }}
                      className="block rounded-control border border-border-soft bg-surface-raised px-3 py-2 transition-colors duration-(--duration-quick) hover:border-border-strong"
                    >
                      <div className="flex items-baseline gap-2 text-2xs text-ink-faint">
                        <span className="font-semibold text-ink">{nameOf(author)}</span>
                        <span>{formatDay(message.createdAt)}</span>
                        {message.userId === meId && <span>· te</span>}
                      </div>
                      <p className="mt-0.5 whitespace-pre-wrap break-words text-sm">{preview}</p>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </ViewTransition>
        )}
      </div>
    </ViewTransition>
  );
}
